import React from "react";
import { useTranslation } from "react-i18next";
import "./lawyers.css";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Must match the workingHours default of the backend lawyer model: lawyers
// who never edited their hours get it, so it is only indicative.
const DEFAULT_HOURS = DAYS.map((day) =>
  day === "Sunday"
    ? [day, "08:00:00", "08:00:00", "08:00:00", "08:00:00"]
    : [day, "08:00:00", "12:00:00", "13:00:00", "17:00:00"]
);

const hhmm = (time) => (time || "").slice(0, 5);

// Open ranges of a day; a range whose start equals its end is closed.
function rangesOf(entry) {
  if (!entry) return [];
  return [
    [entry.startTimeMorning, entry.endTimeMorning],
    [entry.startTimeAfternoon, entry.endTimeAfternoon],
  ]
    .map(([from, to]) => [hhmm(from), hhmm(to)])
    .filter(([from, to]) => from && to && from < to);
}

function usesDefaultHours(workingHours) {
  const key = (entry) => [
    entry.day,
    entry.startTimeMorning,
    entry.endTimeMorning,
    entry.startTimeAfternoon,
    entry.endTimeAfternoon,
  ];
  return (
    workingHours.length === DEFAULT_HOURS.length &&
    workingHours.every((entry, i) => key(entry).join() === DEFAULT_HOURS[i].join())
  );
}

export default function LawyerHours({ lawyer }) {
  const { t } = useTranslation();
  const workingHours = lawyer.workingHours || [];
  if (workingHours.length === 0) return null;

  const now = new Date();
  const today = DAYS[now.getDay()];
  const nowTime = now.toTimeString().slice(0, 5);
  const byDay = Object.fromEntries(workingHours.map((entry) => [entry.day, entry]));
  const openNow = rangesOf(byDay[today]).some(
    ([from, to]) => nowTime >= from && nowTime < to
  );
  // Default hours were never confirmed by the lawyer: no open/closed claim.
  const indicative = usesDefaultHours(workingHours);
  // Show the week starting on Monday, as is usual in Tunisia.
  const week = [...DAYS.slice(1), DAYS[0]];

  return (
    <div className="avc-sidebar-card avc-hours">
      <h2 className="avc-sidebar-card__title avc-hours__title">
        {t("Business_Hours")}
        {!indicative && (
          <span className={"avc-hours__status" + (openNow ? " is-open" : "")}>
            {openNow ? t("Open_Now") : t("Closed_Now")}
          </span>
        )}
      </h2>
      <ul className="avc-hours__list">
        {week.map((day) => {
          const ranges = rangesOf(byDay[day]);
          return (
            <li key={day} className={day === today ? "is-today" : undefined}>
              <span className="avc-hours__day">
                {t(day)}
                {day === today && <small>{t("Today")}</small>}
              </span>
              <span className="avc-hours__time" dir="ltr">
                {ranges.length === 0
                  ? t("Closed")
                  : ranges.map(([from, to]) => `${from} – ${to}`).join(" · ")}
              </span>
            </li>
          );
        })}
      </ul>
      {indicative && (
        <p className="avc-hours__note">{t("profile_hours_indicative")}</p>
      )}
    </div>
  );
}
